import React, { useMemo, useState, useCallback, useEffect } from "react";
import "./MetaLiftObjectiveCell.scss";
import { MdDelete } from "react-icons/md";
import { StandardEvents } from "./MetaLiftStudyUtils";
import { Dropdown, DropdownToggleType, MenuItem } from "../Components/Dropdown/Dropdown";

export interface CustomConversionOption {
  custom_conversion_id: string;
  name: string;
}

export interface CustomEventOption {
  event_name: string;
}

interface MetaLiftObjectiveCellProps {
  cellId: string;
  cellName: string;
  eventNames: string[];
  customConversions: string[];
  onCellNameChange: (newName: string) => void;
  onSelectionApply: (eventNames: string[], customConversions: string[]) => void;
  onDelete: () => void;
  canDelete: boolean;
  customConversionsList?: CustomConversionOption[];
  customEventsList?: CustomEventOption[];
}

const MetaLiftObjectiveCell = ({
  cellName,
  eventNames,
  customConversions,
  onCellNameChange,
  onSelectionApply,
  onDelete,
  canDelete,
  customConversionsList = [],
  customEventsList = [],
}: MetaLiftObjectiveCellProps): JSX.Element => {
  const allSelected = useMemo(
    () => [...eventNames, ...customConversions],
    [eventNames, customConversions]
  );
  const [pendingSelection, setPendingSelection] = useState<string[]>(allSelected);

  const SHOW_CUSTOM_OPTIONS = true;

  const allEventOptions = useMemo(() => {
    const standardOptions = StandardEvents.map(event => ({
      label: event.label,
      value: event.value,
    }));
    const standardLabels = new Set(StandardEvents.map(event => event.label));
    const customConversionsOptions = customConversionsList.map(cc => ({
      label: cc.name,
      value: cc.custom_conversion_id,
    }));
    const customConversionsNames = new Set(customConversionsList.map(cc => cc.name));
    const customEventsOptions = customEventsList
      .filter(event => !standardLabels.has(event.event_name))
      .filter(event => !customConversionsNames.has(event.event_name))
      .map(event => ({
        label: event.event_name,
        value: event.event_name,
      }));
    return [
      ...standardOptions,
      ...(SHOW_CUSTOM_OPTIONS ? customConversionsOptions : []),
      ...(SHOW_CUSTOM_OPTIONS ? customEventsOptions : []),
    ];
  }, [customConversionsList, customEventsList, SHOW_CUSTOM_OPTIONS]);

  const menuItems = useMemo<MenuItem<string>[]>(
    () =>
      allEventOptions.map(option => ({
        label: option.label,
        value: option.value,
        selected: pendingSelection.includes(option.value),
      })),
    [allEventOptions, pendingSelection]
  );

  const customConversionIds = useMemo(
    () => new Set(customConversionsList.map(cc => cc.custom_conversion_id)),
    [customConversionsList]
  );

  const dropdownLabel = useMemo(() => {
    if (allSelected.length === 0) {
      return "Select sources";
    }
    if (allSelected.length === 1) {
      const option = allEventOptions.find(e => e.value === allSelected[0]);
      return option?.label || allSelected[0];
    }
    return `${allSelected.length} events selected`;
  }, [allSelected, allEventOptions]);

  const handleToggleEvent = useCallback((value: string) => {
    setPendingSelection(prev => (prev.includes(value) ? [] : [value]));
  }, []);

  const handleApply = useCallback(() => {
    const nextEventNames: string[] = [];
    const nextCustomConversions: string[] = [];
    for (const value of pendingSelection) {
      if (customConversionIds.has(value)) {
        nextCustomConversions.push(value);
      } else {
        nextEventNames.push(value);
      }
    }
    onSelectionApply(nextEventNames, nextCustomConversions);
  }, [pendingSelection, customConversionIds, onSelectionApply]);

  const handleCancel = useCallback(() => {
    setPendingSelection(allSelected);
  }, [allSelected]);

  useEffect(() => {
    setPendingSelection(allSelected);
  }, [allSelected]);

  return (
    <div className="metaLiftObjectiveCellOuter">
      <div className="metaLiftObjectiveCellInner">
        <div className="metaLiftObjectiveCellInnerHeader">
          <input
            type="text"
            className="metaLiftObjectiveCellInnerHeaderTitle"
            value={cellName}
            onChange={e => onCellNameChange(e.target.value)}
            placeholder="Objective Name"
          />
          <MdDelete
            className={
              canDelete
                ? "metaLiftObjectiveCellDeleteButton"
                : "metaLiftObjectiveCellDeleteButton metaLiftObjectiveCellDeleteButton--disabled"
            }
            style={{ cursor: canDelete ? "pointer" : "not-allowed" }}
            onClick={canDelete ? onDelete : undefined}
            aria-label={
              canDelete
                ? "Delete objective cell"
                : "Cannot delete - at least one objective is required"
            }
            tabIndex={canDelete ? 0 : -1}
            role="button"
            size={25}
          />
        </div>

        <div className="metaLiftConversionSourceHolder">
          <div className="metaLiftConversionSourceHolderTitle">Conversion Events</div>

          <Dropdown
            type={DropdownToggleType.OUTLINED}
            background="light"
            size="lg"
            label={dropdownLabel}
            options={[]}
            menuItems={menuItems}
            hasTicks
            onChange={handleToggleEvent}
            applyOnClick={handleApply}
            cancelOnClick={handleCancel}
            dropdownMenuClassName="metaLiftObjectiveCellDropdownMenu"
          />
        </div>
      </div>
    </div>
  );
};

export default MetaLiftObjectiveCell;
